import { supabase } from "@/integrations/supabase/client";
import { parseGenres } from "@/components/ui/genre-selector";

interface GameData {
  id: string;
  title: string;
  description: string;
  genre?: string;
  cover_image_url?: string;
  screenshots?: string[];
  steam_url?: string;
  price?: number;
  release_date?: string;
  developer_id: string;
  youtube_trailer_url?: string;
      developer?: {
        display_name: string;
        studio_name?: string;
        avatar_url?: string;
        first_name?: string;
        last_name?: string;
        email?: string;
        country?: string;
      };
}

export async function generatePressKit(gameId: string, userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log("Generating press kit for game:", gameId, "by user:", userId);
    
    // First, try to fetch the game without the relation to check if it exists
    const { data: gameBasic, error: basicError } = await supabase
      .from("games")
      .select("*")
      .eq("id", gameId)
      .single();

    if (basicError) {
      console.error("Error fetching game (basic):", basicError);
      return { success: false, error: `Game not found: ${basicError.message}` };
    }

    if (!gameBasic) {
      console.error("Game not found:", gameId);
      return { success: false, error: "Game not found" };
    }

    // Now fetch developer info separately
    const { data: developer, error: devError } = await supabase
      .from("profiles")
      .select("display_name, studio_name, avatar_url, first_name, last_name, country, bio, twitch_url, youtube_url, tiktok_url, instagram_url, twitter_url, bluesky_url, steam_url, website_url")
      .eq("id", gameBasic.developer_id)
      .single();

    if (devError) {
      console.warn("Error fetching developer info:", devError);
      // Continue anyway, developer info is optional
    }

    // Fetch developer email - CRITICAL: Must succeed
    let developerEmail: string | undefined;
    
    // Method 1: If current user IS the developer, use their session email (easiest)
    if (userId === gameBasic.developer_id) {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser?.email) {
          developerEmail = currentUser.email;
          console.log("✅ Email from current user session (developer themselves):", developerEmail);
        }
      } catch (e: any) {
        console.warn("⚠️ Could not get current user email:", e.message);
      }
    }
    
    // Method 2: If not the developer, use admin RPC function
    if (!developerEmail) {
      try {
        console.log("🔍 Attempting to retrieve email via RPC for developer:", gameBasic.developer_id);
        const { data: emailData, error: emailError } = await supabase.rpc('get_user_email', { 
          user_id: gameBasic.developer_id 
        });
        
        if (emailError) {
          console.error("❌ RPC Error details:", {
            message: emailError.message,
            details: emailError.details,
            hint: emailError.hint,
            code: emailError.code
          });
        } else if (emailData) {
          developerEmail = emailData;
          console.log("✅ Email retrieved via RPC:", developerEmail);
        } else {
          console.warn("⚠️ RPC returned no data and no error");
        }
      } catch (error: any) {
        console.error("❌ Exception calling RPC:", error.message, error);
      }
    }
    
    // CRITICAL: Email is absolutely required - fail if not found
    if (!developerEmail || !developerEmail.includes('@')) {
      const errorMsg = `Impossible de récupérer l'email du développeur. ID développeur: ${gameBasic.developer_id}. Utilisateur actuel: ${userId}. Veuillez vérifier que vous avez les droits administrateur et que le compte développeur a un email valide.`;
      console.error("❌❌❌", errorMsg);
      return { success: false, error: errorMsg };
    }
    
    console.log("✅✅✅ EMAIL CONFIRMÉ:", developerEmail);

    // Ensure developerEmail is set before building gameData
    const gameData: GameData = {
      ...gameBasic,
      developer: developer ? {
        ...developer,
        email: developerEmail // This is now guaranteed to be set
      } : {
        display_name: "Unknown Developer",
        email: developerEmail // Still set email even if developer info is missing
      }
    } as GameData;

    // Extract features from description (improved extraction)
    const features: string[] = [];
    const descriptionText = gameData.description.replace(/<[^>]*>/g, ''); // Remove HTML tags
    
    // Helper function to clean and validate a feature
    const cleanFeature = (text: string): string | null => {
      // Remove leading/trailing whitespace and punctuation
      let cleaned = text.trim();
      
      // Remove leading commas, periods, dashes, or other punctuation
      cleaned = cleaned.replace(/^[,.\-\s]+/, '');
      
      // Remove trailing commas, periods (but keep exclamation/question marks if meaningful)
      cleaned = cleaned.replace(/[,\.]+$/, '');
      
      // Must start with a letter or number (not punctuation)
      if (!/^[a-zA-Z0-9]/.test(cleaned)) {
        return null;
      }
      
      // Must be a reasonable length (at least 10 chars, max 200)
      if (cleaned.length < 10 || cleaned.length > 200) {
        return null;
      }
      
      // Don't include URLs
      if (cleaned.includes('http://') || cleaned.includes('https://') || cleaned.includes('www.')) {
        return null;
      }
      
      // Don't include email addresses
      if (cleaned.includes('@')) {
        return null;
      }
      
      // Capitalize first letter
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
      
      return cleaned;
    };
    
    // Try to extract bullet points or features from lines
    const lines = descriptionText.split('\n').map(l => l.trim()).filter(Boolean);
    lines.forEach(line => {
      // Look for bullet points or numbered lists
      if (line.match(/^[•\-\*]\s+/) || line.match(/^\d+[\.\)]\s+/)) {
        const feature = cleanFeature(line.replace(/^[•\-\*\d\.\)]\s+/, ''));
        if (feature && features.length < 10) {
          features.push(feature);
        }
      } else if (line.length > 20 && line.length < 200 && !line.includes('http')) {
        // Potential feature if it's a reasonable length and looks like a complete sentence
        // Check if it starts with a capital letter or can be capitalized
        const feature = cleanFeature(line);
        if (feature && features.length < 10) {
          features.push(feature);
        }
      }
    });

    // If no features found, try to extract from sentences
    if (features.length === 0) {
      // Split by sentence endings, but be more careful
      const sentences = descriptionText
        .split(/[.!?]+/)
        .map(s => s.trim())
        .filter(s => {
          // Filter out very short or very long sentences
          const length = s.length;
          return length >= 20 && length <= 200;
        })
        .filter(s => {
          // Filter out sentences that start with punctuation
          return /^[a-zA-Z0-9]/.test(s);
        })
        .slice(0, 5);
      
      sentences.forEach(sentence => {
        const feature = cleanFeature(sentence);
        if (feature && features.length < 10) {
          features.push(feature);
        }
      });
    }

    // Parse genres
    const genres = gameData.genre ? parseGenres(gameData.genre) : [];

    // Build links object
    const links: Record<string, string> = {};
    if (gameData.steam_url) {
      links.steam = gameData.steam_url;
    }

    // Build contact object - email is MANDATORY and already validated
    const contact: Record<string, string> = {
      email: developerEmail // Directly use developerEmail which is guaranteed to be set
    };
    
    // Add name (first + last name, or display_name as fallback)
    if (gameData.developer?.first_name || gameData.developer?.last_name) {
      const fullName = [gameData.developer.first_name, gameData.developer.last_name]
        .filter(Boolean)
        .join(' ');
      if (fullName) {
        contact.name = fullName;
      }
    } else if (gameData.developer?.display_name) {
      contact.name = gameData.developer.display_name;
    }
    
    if (gameData.developer?.studio_name) {
      contact.studio_name = gameData.developer.studio_name;
    } else if (gameData.developer?.display_name) {
      contact.studio_name = gameData.developer.display_name;
    }
    if (gameData.developer?.avatar_url) {
      contact.studio_logo = gameData.developer.avatar_url;
    }
    if (gameData.developer?.country) {
      contact.country = gameData.developer.country;
    }

    // Build studio social media links
    const studioSocialMedia: Record<string, string> = {};
    if (developer?.twitch_url) studioSocialMedia.twitch = developer.twitch_url;
    if (developer?.youtube_url) studioSocialMedia.youtube = developer.youtube_url;
    if (developer?.tiktok_url) studioSocialMedia.tiktok = developer.tiktok_url;
    if (developer?.instagram_url) studioSocialMedia.instagram = developer.instagram_url;
    if (developer?.twitter_url) studioSocialMedia.twitter = developer.twitter_url;
    if (developer?.bluesky_url) studioSocialMedia.bluesky = developer.bluesky_url;
    if (developer?.steam_url) studioSocialMedia.steam = developer.steam_url;
    if (developer?.website_url) studioSocialMedia.website = developer.website_url;


    // Build press kit content
    const pressKitContent: any = {
      title: gameData.title,
      description: gameData.description,
      screenshots: gameData.screenshots || [],
      links,
      contact,
      features: features.slice(0, 10), // Limit to 10 features
      release_date: gameData.release_date,
      genres,
      price: gameData.price
    };

    // Add optional fields only if they exist
    if (gameData.youtube_trailer_url) {
      pressKitContent.trailer = gameData.youtube_trailer_url;
    }
    
    if (developer?.bio) {
      pressKitContent.studio_description = developer.bio;
    }
    
    if (Object.keys(studioSocialMedia).length > 0) {
      pressKitContent.studio_social_media = studioSocialMedia;
    }


    // Check if press kit already exists
    const { data: existing, error: checkError } = await supabase
      .from("presskits")
      .select("id")
      .eq("game_id", gameId)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error("Error checking existing press kit:", checkError);
      return { success: false, error: `Error checking existing press kit: ${checkError.message}` };
    }

    if (existing) {
      // Update existing press kit
      const { error: updateError } = await supabase
        .from("presskits")
        .update({
          content: pressKitContent,
          email: developerEmail, // Store email directly in the table - REQUIRED
          generated_by: userId,
          updated_at: new Date().toISOString()
        })
        .eq("game_id", gameId);

      if (updateError) {
        console.error("❌ Error updating press kit:", updateError);
        console.error("❌ Error details:", JSON.stringify(updateError, null, 2));
        console.error("❌ Error code:", updateError.code);
        console.error("❌ Error message:", updateError.message);
        console.error("❌ Error hint:", updateError.hint);
        return { success: false, error: `Failed to update press kit: ${updateError.message}` };
      }
      
      // Verify the update was successful by fetching the updated press kit
      const { data: updatedPressKit, error: verifyError } = await supabase
        .from("presskits")
        .select("content")
        .eq("game_id", gameId)
        .single();
      
      if (verifyError) {
        console.warn("⚠️ Could not verify update:", verifyError);
      }
    } else {
      console.log("Creating new press kit with email:", developerEmail);
      // Create new press kit
      const { error: insertError } = await supabase
        .from("presskits")
        .insert({
          game_id: gameId,
          content: pressKitContent,
          email: developerEmail, // Store email directly in the table - REQUIRED
          generated_by: userId,
          is_public: true
        });

      if (insertError) {
        console.error("Error inserting press kit:", insertError);
        return { success: false, error: `Failed to create press kit: ${insertError.message}` };
      }
      console.log("✅ Press kit created successfully with email:", developerEmail);
    }

    return { success: true };
  } catch (error: any) {
    console.error("Error generating press kit:", error);
    return { success: false, error: error.message || "Failed to generate press kit" };
  }
}

export async function checkPressKitExists(gameId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("presskits")
      .select("id")
      .eq("game_id", gameId)
      .eq("is_public", true)
      .single();

    return !error && !!data;
  } catch {
    return false;
  }
}

